/**
 * System › Message Library — every call goes through the ONE shared axios client
 * (§18.6), which attaches the JWT, the division header, the global loader and the
 * `X-Audit-Reason` the shared save flow arms.
 */
import { api } from '../../../api/client';
import type {
  MessageDetail, MessageHistoryRow, MessageInput, MessageRow, ModuleOption, Vocabularies,
} from './types';
import { MESSAGE_FORM_NAME } from './messageLibraryConfig';

export const MESSAGES_BASE = '/api/v1/messages';

/** Unwraps the standard `{ success, data }` envelope (§6.10). */
function dataOf<T>(body: unknown, fallback: T): T {
  const d = (body as { data?: T } | undefined)?.data;
  return d === undefined || d === null ? fallback : d;
}

export async function fetchMessages(): Promise<MessageRow[]> {
  const res = await api.get(MESSAGES_BASE);
  return dataOf<MessageRow[]>(res.data, []);
}

export async function fetchMessage(id: string): Promise<MessageDetail | null> {
  const res = await api.get(`${MESSAGES_BASE}/${id}`);
  const d = dataOf<MessageDetail | null>(res.data, null);
  // The usage arrays are read off an embedded scan, so a payload that predates
  // it — or one where the scan found nothing — can omit them. Normalise here,
  // once, rather than defending at each of the several places that count them:
  // a missing array is "no usage", never a crashed detail view.
  return d && {
    ...d,
    usedInForms: d.usedInForms ?? [],
    usedInFiles: d.usedInFiles ?? [],
    usedIn: d.usedIn ?? [],
    translations: d.translations ?? [],
  };
}

/**
 * This message's audit trail — the Change History tab.
 *
 * Reads the SHARED `/api/v1/audit/trail` endpoint that every other form's audit
 * log uses (standing rule 8), scoped to this record by `sourceId`. The writes it
 * reads back are the ones the controller already makes under FormName
 * "Message Library", so nothing new is persisted for this tab to exist.
 */
export async function fetchMessageHistory(oid: string): Promise<MessageHistoryRow[]> {
  const res = await api.get('/api/v1/audit/trail', {
    params: { formName: MESSAGE_FORM_NAME, sourceId: oid },
  });
  return dataOf<MessageHistoryRow[]>(res.data, []);
}

export async function fetchModules(): Promise<ModuleOption[]> {
  const res = await api.get(`${MESSAGES_BASE}/modules`);
  return dataOf<ModuleOption[]>(res.data, []);
}

/** The BO list for one Module — the legacy cascade. */
export async function fetchBos(moduleId: string): Promise<ModuleOption[]> {
  const res = await api.get(`${MESSAGES_BASE}/bos`, { params: { moduleId } });
  return dataOf<ModuleOption[]>(res.data, []);
}

export async function fetchVocabularies(): Promise<Vocabularies> {
  const res = await api.get(`${MESSAGES_BASE}/vocabularies`);
  return dataOf<Vocabularies>(res.data, { severities: [], actionTypes: [], languages: [] });
}

export async function createMessage(input: MessageInput): Promise<{ id: string; msid: string }> {
  const res = await api.post(MESSAGES_BASE, input);
  return dataOf<{ id: string; msid: string }>(res.data, { id: '', msid: '' });
}

export async function updateMessage(id: string, input: MessageInput): Promise<void> {
  await api.put(`${MESSAGES_BASE}/${id}`, input);
}

export async function deleteMessage(id: string): Promise<void> {
  await api.delete(`${MESSAGES_BASE}/${id}`);
}

/** Bulk Module / BO assignment. Returns how many rows changed. */
export async function assignModule(
  oids: string[], moduleId: string | null, boId: string | null,
): Promise<number> {
  const res = await api.post(`${MESSAGES_BASE}/assign-module`, { oids, moduleId, boId });
  return dataOf<{ changed: number }>(res.data, { changed: 0 }).changed;
}

export async function addTranslation(
  messageId: string, language: string, translation: string,
): Promise<void> {
  await api.post(`${MESSAGES_BASE}/${messageId}/translations`, { language, translation });
}

export async function updateTranslation(
  messageId: string, translationId: string, language: string, translation: string,
): Promise<void> {
  await api.put(`${MESSAGES_BASE}/${messageId}/translations/${translationId}`, { language, translation });
}

export async function deleteTranslation(
  messageId: string, translationId: string,
): Promise<void> {
  await api.delete(`${MESSAGES_BASE}/${messageId}/translations/${translationId}`);
}

/** One row of an Import file, after parsing. */
export interface MessageImportRow {
  messageKey: string;
  message: string;
  /** Absent when the file has no Remark column — the server leaves it alone. */
  remark?: string | null;
}

/** What the server did with an Import. */
export interface MessageImportResult {
  matched: number;
  updated: number;
  unknownKeys: number;
  emptyMessages: number;
}

/** Applies reviewed wording from a spreadsheet. The server matches on
 *  MessageKey, writes only Message / Remark, and counts every skip. */
export async function importMessages(rows: MessageImportRow[]): Promise<MessageImportResult> {
  const res = await api.post(`${MESSAGES_BASE}/import`, { rows });
  return dataOf<MessageImportResult>(res.data, {
    matched: 0, updated: 0, unknownKeys: 0, emptyMessages: 0,
  });
}

/** What a catalog sync did. */
export interface CatalogSyncResult {
  catalogEntries: number;
  inserted: number;
  updated: number;
  customizedKept: number;
  reactivated: number;
  retired: number;
  skipped: number;
  noChanges: boolean;
}

/** Runs the code-catalog sync on demand — the same reconciliation the API
 *  performs at startup. Idempotent: a second run over unchanged catalogs
 *  changes nothing. */
export async function syncCatalog(): Promise<CatalogSyncResult> {
  const res = await api.post(`${MESSAGES_BASE}/sync-catalog`);
  return dataOf<CatalogSyncResult>(res.data, {
    catalogEntries: 0, inserted: 0, updated: 0, customizedKept: 0,
    reactivated: 0, retired: 0, skipped: 0, noChanges: true,
  });
}
